interface LightboxItem {
    src: string;
    caption: string;
    trigger: HTMLAnchorElement;
}

const ICON_CLOSE =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" aria-hidden="true"><path d="M28 2.8 25.2 0 14 11.2 2.8 0 0 2.8 11.2 14 0 25.2 2.8 28 14 16.8 25.2 28l2.8-2.8L16.8 14 28 2.8z"/></svg>';
const ICON_PREV =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14.8 24" aria-hidden="true"><path d="M14.8 21.2 5.7 12l9.2-9.2L12 0 0 12l12 12 2.8-2.8z"/></svg>';
const ICON_NEXT =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14.8 24" aria-hidden="true" style="transform: rotate(180deg)"><path d="M14.8 21.2 5.7 12l9.2-9.2L12 0 0 12l12 12 2.8-2.8z"/></svg>';

export class LightboxComponent {
    private overlay: HTMLElement;
    private image: HTMLImageElement;
    private caption: HTMLElement;
    private counter: HTMLElement;
    private closeButton: HTMLButtonElement;
    private prevButton: HTMLButtonElement;
    private nextButton: HTMLButtonElement;

    private items: Array<LightboxItem> = [];
    private index = 0;
    private lastFocused: HTMLElement = null;
    private scrollPosition = 0;

    constructor() {
        const galleries = Array.from(document.querySelectorAll('[data-gallery]'));
        if (galleries.length === 0) return;

        this.createOverlay();

        galleries.forEach((gallery: HTMLElement) => {
            const triggers = Array.from(
                gallery.querySelectorAll('[data-gallery-item]')
            ) as Array<HTMLAnchorElement>;

            const items: Array<LightboxItem> = triggers.map((trigger) => ({
                src: trigger.getAttribute('data-full') || trigger.href,
                caption: trigger.getAttribute('data-caption') || '',
                trigger: trigger,
            }));

            triggers.forEach((trigger, i) => {
                trigger.addEventListener('click', (e: MouseEvent) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    this.open(items, i);
                });
            });
        });
    }

    private createOverlay() {
        this.overlay = document.createElement('div');
        this.overlay.className = 'lightbox';
        this.overlay.setAttribute('role', 'dialog');
        this.overlay.setAttribute('aria-modal', 'true');
        this.overlay.setAttribute('aria-label', 'Foto');
        this.overlay.setAttribute('aria-hidden', 'true');

        this.counter = document.createElement('p');
        this.counter.className = 'lightbox__counter';

        this.closeButton = this.createButton('lightbox__close', 'Sluiten', ICON_CLOSE);
        this.prevButton = this.createButton('lightbox__prev', 'Vorige foto', ICON_PREV);
        this.nextButton = this.createButton('lightbox__next', 'Volgende foto', ICON_NEXT);

        const figure = document.createElement('figure');
        figure.className = 'lightbox__figure';

        this.image = document.createElement('img');
        this.image.className = 'lightbox__image';
        this.image.alt = '';

        this.caption = document.createElement('figcaption');
        this.caption.className = 'lightbox__caption';

        figure.appendChild(this.image);
        figure.appendChild(this.caption);

        this.overlay.appendChild(this.counter);
        this.overlay.appendChild(figure);
        this.overlay.appendChild(this.closeButton);
        this.overlay.appendChild(this.prevButton);
        this.overlay.appendChild(this.nextButton);

        document.body.appendChild(this.overlay);

        this.closeButton.addEventListener('click', () => this.close());
        this.prevButton.addEventListener('click', () => this.goTo(this.index - 1));
        this.nextButton.addEventListener('click', () => this.goTo(this.index + 1));

        this.overlay.addEventListener('click', (e: MouseEvent) => {
            if (e.target === this.overlay || e.target === figure) {
                this.close();
            }
        });

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (!this.isOpen()) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                this.close();
            }
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this.goTo(this.index - 1);
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                this.goTo(this.index + 1);
            }
            if (e.key === 'Tab') {
                this.trapFocus(e);
            }
        });
    }

    private createButton(className: string, label: string, icon: string): HTMLButtonElement {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `lightbox__button ${className}`;
        button.setAttribute('aria-label', label);
        button.innerHTML = icon;
        return button;
    }

    private isOpen(): boolean {
        return this.overlay.classList.contains('lightbox--open');
    }

    private open(items: Array<LightboxItem>, index: number) {
        this.items = items;
        this.lastFocused = document.activeElement as HTMLElement;

        // Locking the body resets the document scroll, so remember where we were.
        this.scrollPosition = window.scrollY;

        this.overlay.classList.add('lightbox--open');
        this.overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('lightbox-open');

        this.goTo(index);
        this.closeButton.focus();
    }

    private close() {
        this.overlay.classList.remove('lightbox--open');
        this.overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('lightbox-open');
        window.scrollTo(0, this.scrollPosition);

        if (this.lastFocused) {
            this.lastFocused.focus({ preventScroll: true });
            this.lastFocused = null;
        }
    }

    private goTo(index: number) {
        if (this.items.length === 0) return;

        this.index = Math.min(Math.max(index, 0), this.items.length - 1);
        const item = this.items[this.index];

        this.image.src = item.src;
        this.image.alt = item.caption;
        this.caption.textContent = item.caption;
        this.caption.hidden = item.caption === '';
        this.counter.textContent = `${this.index + 1} / ${this.items.length}`;

        const single = this.items.length < 2;
        this.prevButton.hidden = single;
        this.nextButton.hidden = single;
        this.counter.hidden = single;
        this.prevButton.disabled = this.index === 0;
        this.nextButton.disabled = this.index === this.items.length - 1;
    }

    private trapFocus(e: KeyboardEvent) {
        const focusable = Array.from(
            this.overlay.querySelectorAll('button:not([disabled]):not([hidden])')
        ) as Array<HTMLElement>;

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }
}
